// For info on making requests - https://postgrest.org/en/v5.0/api.html

import { API_ENDPOINT } from 'src/store/firebase'

import dates from 'src/utils/dates'
import querystring from 'src/utils/querystring'

import http from './http'
import server from './server'

// Auto-enable stub mode on localhost — the real API rejects CORS for localhost origins.
const IS_LOCAL_DEV = typeof window !== 'undefined'
  && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')

// Auto-enable stub mode when there's no real legacy API behind this deploy
// (e.g. iadops-try, see .env.try) rather than the user's demo-mode preference.
const IS_API_STUBBED = process.env.VUE_APP_FORCE_API_STUBS === 'true'

// Request cache to prevent redundant API calls
const requestCache = new Map()
const pendingRequests = new Map()

// Cache TTL: 5 minutes for most requests, 30 seconds for time-sensitive data
const CACHE_TTL = {
  default: 5 * 60 * 1000, // 5 minutes
  timesheet: 30 * 1000, // 30 seconds for time entries
}

/**
 * Invalidate cache entries matching a URI pattern
 * @param {String|RegExp} pattern - URI pattern to match (string prefix or RegExp)
 * @example
 * invalidateCache('/projects') // Clears all cache entries starting with '/projects'
 * invalidateCache(/\/projects/) // Clears all cache entries matching the regex
 */
function invalidateCache(pattern) {
  const keys = Array.from(requestCache.keys())

  if (typeof pattern === 'string') {
    // Clear all entries that start with the pattern
    keys
      .filter((key) => key.startsWith(pattern))
      .forEach((key) => requestCache.delete(key))
  } else if (pattern instanceof RegExp) {
    // Clear all entries matching the regex
    keys
      .filter((key) => pattern.test(key))
      .forEach((key) => requestCache.delete(key))
  }
}

/**
 * Clear the entire request cache
 */
function clearAllCache() {
  requestCache.clear()
}

/**
 * Cached GET request wrapper
 * Deduplicates concurrent requests and caches results to prevent redundant API calls
 * @param {Function} getFunc - The HTTP GET function
 * @param {String} uri - The request URI
 * @param {Object} config - Request configuration
 * @param {Number} ttl - Cache time-to-live in milliseconds
 * @returns {Promise} The API response
 */
async function cachedGet(getFunc, uri, config = {}, ttl = CACHE_TTL.default) {
  const cacheKey = `${uri}${JSON.stringify(config)}`

  // Return cached response if still valid
  const cached = requestCache.get(cacheKey)
  if (cached && Date.now() - cached.timestamp < ttl) {
    return cached.data
  }

  // Return pending request if already in flight (deduplication)
  const pending = pendingRequests.get(cacheKey)
  if (pending) {
    return pending
  }

  // Make new request
  const promise = getFunc(uri, config)
    .then((data) => {
      // Cache successful response
      requestCache.set(cacheKey, {
        data,
        timestamp: Date.now(),
      })
      pendingRequests.delete(cacheKey)
      return data
    })
    .catch((error) => {
      // Don't cache errors
      pendingRequests.delete(cacheKey)
      throw error
    })

  pendingRequests.set(cacheKey, promise)
  return promise
}

const api = {
  demo: false,

  // Cache management methods
  invalidateCache,
  clearAllCache,

  get: null,
  post: null,
  put: null,

  // @TODO Assert request is allowed in demo mode, else reject request.
  async stub(method) {
    const { default: stub } = await import('./stub')

    return (uri, config) => {
      const useStub = stub.request(uri, config)
      return useStub || http[method](uri, config)
    }
  },
  async set(demo) {
    // On localhost the real API rejects CORS, and on deploys with no real
    // legacy API behind them, always use stubs regardless of the user's
    // demo-mode preference.
    const useDemo = IS_LOCAL_DEV || IS_API_STUBBED || demo
    api.demo = useDemo

    if (useDemo) {
      const promises = [api.stub('get'), api.stub('post'), api.stub('put')]
      const [get, post, put] = await Promise.all(promises)

      api.get = get
      api.post = post
      api.put = put
    } else {
      // Wrap GET requests with caching to prevent redundant API calls
      const httpGet = http.get.bind(http)
      api.get = (uri, config) => cachedGet(httpGet, uri, config)
      api.post = http.post.bind(http)
      api.put = http.put.bind(http)
    }
  },

  getLimitAndOffset({ limit, offset }) {
    const setLimit = typeof limit === 'number' ? `&limit=${limit}` : ''
    const setOffset = typeof offset === 'number' ? `&offset=${offset}` : ''
    return `${setLimit}${setOffset}`
  },
  getParameters(params) {
    const validProps = {
      clicks: { apiKey: 'clicks' },
      ctr: { apiKey: 'ctr' },
      delivered: { apiKey: 'impressionsYesterday' },
      endDate: { apiKey: 'endDate' },
      isMissingCreatives: { apiKey: 'isMissingCreatives' },
      osi: { apiKey: 'osi' },
      priority: { apiKey: 'type' },
      startDate: { apiKey: 'startDate' },
      status: { apiKey: 'status' },
    }
    const apiOperators = {
      eq: 'eq',
      excl: 'neq',
      gte: 'gte',
      incl: 'eq',
      is: 'is',
      lte: 'lte',
      neq: 'neq',
      not: 'not',
      range: 'range',
    }
    const propNames = Object.keys(validProps)
    const results = propNames.reduce((paramArray, currentPropName) => {
      const { apiKey } = validProps[currentPropName]
      const currentProp = params[currentPropName]

      if (!currentProp) return paramArray
      if (Array.isArray(currentProp) && !currentProp.length) return paramArray

      const { and, operator, value } = currentProp
      const getAllValues = (property) => property.map((prop) => {
        const { operator: optionOperator, value: optionValue } = prop

        return `${apiKey}=${apiOperators[optionOperator]}.${optionValue}`
      })
      const getValueByType = (val) => {
        const getAll = Array.isArray(val)

        return getAll
          ? getAllValues(val).join('&')
          : `${apiKey}=${apiOperators[operator]}.${val}`
      }
      const queryStringParam = Array.isArray(currentProp)
        ? getAllValues(currentProp).join('&')
        : getValueByType(value)

      if (and) {
        paramArray.push(`and=(${queryStringParam})`)
      } else {
        paramArray.push(queryStringParam)
      }

      return paramArray
    }, [])

    return results.length ? results.join('&') : ''
  },

  getDataRefresh() {
    return this.get('reports/data_refresh')
  },

  submitFeedback(payload) {
    return server.submitFeedback(payload)
  },

  getAdminFeedbackEntries(params = {}) {
    return server.listFeedbackEntries(params)
  },

  updateAdminFeedbackEntry(feedbackId, data = {}) {
    return server.updateFeedbackEntry(feedbackId, data)
  },

  getFeedbackNotificationConfig() {
    return server.getFeedbackNotificationConfig()
  },

  updateFeedbackNotificationConfig(data = {}) {
    return server.updateFeedbackNotificationConfig(data)
  },

  /**
   * Gets data for the AdX Health Report.
   * @param {string[]} dateRange
   * @param {number|string} publisherId
   * @return {Promise<ApiResponse>}
   */
  getHealthAdx({ dateRange, publisherId }) {
    const endpoint = '/reports/programmatic_health_adx'
    const dateQuery = querystring.dates(dateRange)

    return this.get(`${endpoint}?publisherId=eq.${publisherId}&${dateQuery}`)
  },
  /**
   * Gets data for the Network Health Report.
   * @param {Array} dateRange
   * @param {number|string} publisherId
   * @return {Promise<ApiResponse>}
   */
  getHealthNetwork({ dateRange, publisherId }) {
    const endpoint = '/reports/programmatic_health_network'
    const dateQuery = querystring.dates(dateRange)

    return this.get(`${endpoint}?publisherId=eq.${publisherId}&${dateQuery}`)
  },
  /**
   * Gets data for the AdX/OB report, fka the Yield Partner Report.
   * @param {Array} dateRange
   * @param {number|string} publisherId
   * @return {Promise<ApiResponse>}
   */
  getYieldPartner({ dateRange, publisherId }) {
    const endpoint = '/reports/programmatic_yield_partner'
    const dateQuery = querystring.dates(dateRange)

    return this.get(`${endpoint}?publisherId=eq.${publisherId}&${dateQuery}`)
  },
  getPublishers(publisherId) {
    const isPublisherIdArray = Array.isArray(publisherId)
    const validPublisherId = publisherId && !isPublisherIdArray
    const validPublisherIdArray = isPublisherIdArray && publisherId.length
    const passedPublisherIds = validPublisherId || validPublisherIdArray

    if (passedPublisherIds) {
      if (isPublisherIdArray) {
        return this.get(`/master_data/publishers?publisherId=in.(${publisherId})`)
      }

      return this.get(`/master_data/publishers?publisherId=eq.${publisherId}`)
    }

    return this.get('/master_data/publishers?isActive=is.true')
  },
  /**
   * Get publishers that have discrepancy reports enabled
   * @returns {Promise<Object>} Publishers with hasDiscrepancy=true
   */
  getPublishersWithDiscrepancy() {
    return this.get('/master_data/publishers?isActive=is.true&hasDiscrepancy=is.true')
  },
  /**
   * Get publishers that have Network Health reports enabled
   * @returns {Promise<Object>} Publishers with hasHealthNetwork=true
   */
  getPublishersWithHealthNetwork() {
    return this.get('/master_data/publishers?isActive=is.true&hasHealthNetwork=is.true')
  },
  /**
   * Get publishers that have ADX Health reports enabled
   * @returns {Promise<Object>} Publishers with hasHealthAdx=true
   */
  getPublishersWithHealthAdx() {
    return this.get('/master_data/publishers?isActive=is.true&hasHealthAdx=is.true')
  },
  /**
   * Get publishers that have Yield Partner (AdX & OB) reports enabled
   * @returns {Promise<Object>} Publishers with hasYieldPartner=true
   */
  getPublishersWithYieldPartner() {
    return this.get('/master_data/publishers?isActive=is.true&hasYieldPartner=is.true')
  },
  getPublishersByBillingCodes(billingCode) {
    const billingCodes = Array.isArray(billingCode)
      ? billingCode.join(',')
      : billingCode

    return this.get(`/master_data/publishers?billingCode=in.(${billingCodes})`)
  },

  findPersonsByEmailContains(email) {
    return this.get(`/timesheet/persons?personEmail=ilike.*${email}*`)
  },
  findPersonsByEmails(emails) {
    const emailString = Array.isArray(emails) ? emails.join(',') : emails
    return this.get(`/timesheet/persons?personEmail=in.(${emailString})`)
  },

  getPersonarchy() {
    return this.post('/timesheet/rpc/get_personarchy')
  },
  getPersonByEmail(personEmail) {
    return this.get(`/timesheet/persons?personEmail=eq.${personEmail}`)
  },
  getPersonById(personId) {
    return this.get(`/timesheet/persons?personId=eq.${personId}`)
  },
  getPersons() {
    return this.get('/timesheet/persons?order=personNameFirst')
  },
  getTeamsByPublisherId(publisherId, teamIds) {
    const teamId = Array.isArray(teamIds) ? teamIds.join(',') : teamIds
    // @deprecate Handle teamId as string '-1' for backwards compatibility with older calls.
    const teamString = teamId && teamId !== -1 && teamId !== '-1' ? `&teamId=in.(${teamId})` : ''
    const query = `?publisherId=eq.${publisherId}${teamString}`

    return this.get(`/master_data/teams${query}`)
  },
  getTeams(publisherId, teamIds) {
    if (publisherId) {
      return this.getTeamsByPublisherId(publisherId, teamIds)
    }

    return this.get('/master_data/teams')
  },
  getClientByBillingCode(billingCode) {
    return this.get(`/timesheet/clients?billingCode=eq.${billingCode}`)
  },
  getClients(clientId) {
    if (clientId) {
      return this.get(`/timesheet/clients?clientId=eq.${clientId}`)
    }

    return this.get('/timesheet/clients?order=clientName')
  },
  getClientAssignments(personId) {
    const query = personId
      ? `?personId=eq.${personId}&activeRoleAssignment=eq.true&order=clientName`
      : '?activeRoleAssignment=eq.true&order=clientName'

    return this.get(`/timesheet/view_client_assignments${query}`)
  },

  getProjectById(projectId) {
    return this.get(`/timesheet/projects?projectId=eq.${projectId}`)
  },
  getProjectByCode(projectCode) {
    return this.get(`/timesheet/projects?projectCode=eq.${projectCode}`)
  },
  getProjectChangeHistory(projectId) {
    return this.get(`/timesheet/change_history?table_name=eq.projects&serial_column=eq.${projectId}&order=created_dt.desc`)
  },
  getProjects() {
    return this.get('/timesheet/projects?order=projectName')
  },

  getServices() {
    return this.get(`/timesheet/${API_ENDPOINT.SERVICES}`)
  },
  getClientServiceAssociations(clientId) {
    if (clientId) {
      return this.get(`/timesheet/view_client_service_assn?clientId=eq.${clientId}`)
    }
    return this.get('/timesheet/view_client_service_assn')
  },
  modifyService(body) {
    // { action_type: String(add|delete|update) }
    // Required for UPDATE & DELETE: { service_id }
    // CREATE requires: { service_name, person_email }
    // UPDATE: can include any as long as include service_id
    return this.post('/timesheet/rpc/modify_service', body)
  },
  modifyClientService(body) {
    // { action_type: String(add|delete|update) }
    // Required: { client_id, service_id, person_email }
    // Optional: { active_flag, service_client_assn_id }
    return this.post('/timesheet/rpc/modify_client_service', body)
  },
  getTimeTypes() {
    return this.get('/timesheet/time_classification_types')
  },

  getInternalTeams() {
    return this.get('/timesheet/teams')
  },

  viewTimeEntries(queryString = '') {
    const endpoint = '/timesheet/view_time_entries'
    return this.get(`${endpoint}${queryString}`)
  },

  /**
   * @function viewTimeEntriesByDateRange
   * @desc View time entries within a specific date range.
   * @param {Array} dateRangeArray - Array containing start and end dates.
   * @param {Object} filters - Additional filters to apply.
   * @returns {Promise<ApiResponse>}
   */
  viewTimeEntriesByDateRange(dateRangeArray = dates.lastMonthToEndOfNextMonthFormatted(), filters) {
    const [startDateValue, endDateValue] = dateRangeArray // [moment, moment]
    const startDate = dates.getFormattedDate(startDateValue) // YYYY-MM-DD
    const endDate = dates.getFormattedDate(endDateValue) // YYYY-MM-DD

    const queryArray = [`?and=(timeEntryDate.gte.${startDate},timeEntryDate.lte.${endDate})`]

    if (typeof filters === 'object' && filters !== null && Object.keys(filters).length) {
      // Loop through filters and append to queryArray.
      Object.keys(filters).forEach((key) => {
        const value = filters[key]

        if (Array.isArray(value)) {
          queryArray.push(`&${key}=in.(${value.join(',')})`)
        } else {
          queryArray.push(`&${key}=eq.${value}`)
        }
      })
    }

    const queryString = queryArray.join('&')

    return this.viewTimeEntries(queryString)
  },
  viewTimeEntriesByPersonId(personIds, dateRangeArray = dates.lastMonthToEndOfNextMonthFormatted()) {
    const [startDateValue, endDateValue] = dateRangeArray // [moment, moment]
    const startDate = dates.getFormattedDate(startDateValue) // YYYY-MM-DD
    const endDate = dates.getFormattedDate(endDateValue) // YYYY-MM-DD

    const queryString = `?personId=in.(${personIds})&and=(timeEntryDate.gte.${startDate},timeEntryDate.lte.${endDate})`

    return this.viewTimeEntries(queryString)
  },

  viewTimeEntriesByBillingCode(billingCode, dateRangeArray = dates.lastMonthToEndOfNextMonthFormatted()) {
    const [startDateValue, endDateValue] = dateRangeArray // [moment, moment]
    const startDate = dates.getFormattedDate(startDateValue) // YYYY-MM-DD
    const endDate = dates.getFormattedDate(endDateValue) // YYYY-MM-DD

    const queryString = `?billingCode=eq.${billingCode}&and=(timeEntryDate.gte.${startDate},timeEntryDate.lte.${endDate})`

    return this.viewTimeEntries(queryString)
  },

  viewTimeEntriesByProjectId(projectId) {
    const endpoint = '/timesheet/view_time_entries'
    return this.get(`${endpoint}?projectId=eq.${projectId}`)
  },

  viewTimeEntriesByProjectIds(projectIds) {
    const endpoint = '/timesheet/view_time_entries'
    return this.get(`${endpoint}?projectId=in.(${projectIds})`)
  },

  viewTimeEntriesByServiceId(serviceId) {
    const endpoint = '/timesheet/view_time_entries'
    return this.get(`${endpoint}?serviceId=${serviceId}`)
  },
  viewTimeEntriesByTeamId(teamId) {
    const endpoint = '/timesheet/view_time_entries'
    return this.get(`${endpoint}?teamId=${teamId}`)
  },
  viewTimeEntriesByTimeEntryDate(timeEntryDate) {
    const endpoint = '/timesheet/view_time_entries'
    return this.get(`${endpoint}?timeEntryDate=${timeEntryDate}`)
  },
  viewTimeEntriesByTimeEntryId(timeEntryId) {
    const endpoint = '/timesheet/view_time_entries'
    return this.get(`${endpoint}?timeEntryId=${timeEntryId}`)
  },

  modifyOrganization(body) {
    // { action_type: String(add|delete|update) }
    // Required for UPDATE & DELETE: { client_id }
    // CREATE requires e/t except clientId: {client_name, billing_code, is_administrative}
    // UPDATE: can include any as long as include clientId
    return this.post('/timesheet/rpc/modify_client', body)
  },
  modifyPerson(body) {
    // { action_type: String(add|delete|update) }
    // Required for UPDATE & DELETE: { person_id }
    // CREATE requires e/t except person_id
    // UPDATE: can include any as long as include person_id
    return this.post('/timesheet/rpc/modify_person', body)
  },
  modifyProject(body) {
    return this.post('/timesheet/rpc/modify_project', body)
  },
  modifyClientProject(body) {
    return this.post('/timesheet/rpc/modify_client_project', body)
  },

  getWeek(time_shift = 0) {
    return this.post('/timesheet/rpc/get_week', { time_shift })
  },
  createTimeEntry(data) {
    // Required Body: { time_entry_date: Date, person_id: Integer, client_id: Integer, time_classification_id: Integer, duration: Integer, person_email: String }
    // Optional Body Parameters: { service_id: Integer, description: String, project_id: Integer }
    return this.post('/timesheet/rpc/add_entry', data)
  },
  updateTimeEntry(data) {
    // Required Body: { time_entry_id: Integer, person_email: String }
    // Optional Body Parameters: { time_entry_date: Date, client_id: Integer, time_classification_id: Integer, duration: Integer, service_id: Integer, description: String, project_id: Integer }
    return this.post('/timesheet/rpc/update_entry', data)
  },
  deleteTimeEntry(data) {
    // Required Body: { time_entry_id: Integer, person_email: String }
    return this.post('/timesheet/rpc/delete_entry', data)
  },
  getEntries(data) {
    // Required Body: { person_email: String, include_delegates: Boolean }
    return this.post('/timesheet/rpc/get_entries', data)
  },

  getLineItemsForPublisher({ limit, offset = 0, publisherId, teamId }) {
    const teamString = teamId && teamId !== '-1' ? teamId : ''
    const limitAndOffset = api.getLimitAndOffset({ limit, offset })

    const queryString = teamString && teamString !== '-1' ? `&teamIds=cs.{${teamString}}` : ''
    const endpointPath = '/reports/pacing_line_items_summary'

    return this.get(`${endpointPath}?publisherId=eq.${publisherId}${queryString}${limitAndOffset}`)
  },
  getPacingDataForNetwork({ limit, publisherId, teamId, params = {} }) {
    // @deprecate Handle teamId as string '-1' for backwards compatibility with older calls.
    const teamString = teamId && teamId !== -1 && teamId !== '-1' ? `&teamIds=cs.{${teamId}}` : ''
    const limitAndOffset = api.getLimitAndOffset({ limit, offset: 0 })
    const queryString = api.getParameters(params)
    const endpointPath = '/reports/pacing_line_items_summary'

    return this.get(`${endpointPath}?publisherId=eq.${publisherId}${teamString}${limitAndOffset}${queryString ? `&${queryString}` : ''}`)
  },
  getDiscrepancyDataForNetwork({ limit, publisherId, teamId, params = {} }) {
    // @deprecate Handle teamId as string '-1' for backwards compatibility with older calls.
    const teamString = teamId && teamId !== -1 && teamId !== '-1' ? `&teamIds=cs.{${teamId}}` : ''
    const limitAndOffset = api.getLimitAndOffset({ limit, offset: 0 })
    const queryString = api.getParameters(params)
    const endpointPath = '/reports/discrepancy_line_items_beta'

    return this.get(`${endpointPath}?publisherId=eq.${publisherId}${teamString}${limitAndOffset}${queryString ? `&${queryString}` : ''}`)
  },
  getOrderById({ orderId, publisherId }) {
    const queryString = orderId && orderId !== '-1' ? `&orderIds=cs.{${orderId}}` : ''
    return this.get(
      `/trafficking_objects/orders?publisherId=eq.${publisherId}${queryString}`,
    )
  },
  getOrdersForPublisher({ limit, offset, publisherId, teamId }) {
    // @deprecate Handle teamId as string '-1' for backwards compatibility with older calls.
    const teamString = teamId && teamId !== -1 && teamId !== '-1' ? `&teamIds=cs.{${teamId}}` : ''
    const limitAndOffset = api.getLimitAndOffset({ limit, offset })
    return this.get(
      `/trafficking_objects/orders?publisherId=eq.${publisherId}${teamString}${limitAndOffset}`,
    )
  },

  getGamOrders(query) {
    const { networkCode } = query

    return this.get(`/gam/orders/${networkCode}`)
  },
  getGamLineItems(query) {
    const { networkCode, orderId } = query

    return this.post(`/gam/line-items/${networkCode}`, { ids: { orderId } })
  },
  getGamLicas(query) {
    const { licaIds, networkCode } = query

    return this.post(`/gam/licas/${networkCode}`, { licaIds })
  },

  postGamCreatives(query) {
    const { ids, networkCode } = query

    return this.post(`/gam/creatives/${networkCode}`, { ids })
  },
  postTearsheet(query) {
    const { creativeId, lineItemId, networkCode, siteUrl } = query

    return this.post(`/gam/licas/${networkCode}/${lineItemId}/${creativeId}`, { siteUrl })
  },
  getPublisherDiscrepancy({ adjuster, publisherId, teamId }) {
    const endpoint = (adjuster && 'discrepancy_line_items') || 'discrepancy_line_items_beta'
    const teamString = teamId && teamId !== '-1' ? `&teamIds=cs.{${teamId}}` : ''
    return this.get(
      `/reports/${endpoint}?publisherId=eq.${publisherId}${teamString}`,
    )
  },
  getDiscrepancyCreatives({ adjuster, lineItemCode, publisherId, teamId }) {
    const endpoint = (adjuster && 'discrepancy_creatives') || 'discrepancy_creatives_beta'
    const teamString = teamId && teamId !== '-1' ? `&teamIds=cs.{${teamId}}` : ''

    return this.get(
      `/reports/${endpoint}?lineItemCode=eq.${lineItemCode}&publisherId=eq.${publisherId}${teamString}`,
    )
  },
  getCreativesReport({ lineItemId, publisherId, teamIds }) {
    const teamString = teamIds && teamIds.length ? `&teamIds=cs.{${teamIds.join(',')}}` : ''

    const endDateValue = dates.now()
    const startDateValue = endDateValue.clone().subtract(10, 'day')
    const startDateFormattedString = dates.getFormattedDate(startDateValue)
    const endDateFormattedString = dates.getFormattedDate(endDateValue)

    const queryString = `publisherId=eq.${publisherId}${teamString}&lineItemId=eq.${lineItemId}&and=(date.gte.${startDateFormattedString},date.lte.${endDateFormattedString})`

    return this.get(`/reports/delivery_creatives?${queryString}`)
  },
  getDeliveryReportDataForLineItemWithDates({
    lineItemId,
    publisherId,
    startDate,
    teamIds,
    endDate,
  }) {
    const teamString = teamIds && teamIds.length ? `&teamIds=cs.{${teamIds}}` : ''
    const startDateFormattedString = dates.getFormattedDate(startDate)
    const endDateFormattedString = dates.getFormattedDate(endDate)

    return this.get(
      `/reports/delivery_line_items?lineItemId=eq.${lineItemId}&and=(date.gte.${startDateFormattedString},date.lte.${endDateFormattedString})&publisherId=eq.${publisherId}${teamString}`,
    )
  },
  getPacingReportDataForLineItemWithDates({
    lineItemId,
    publisherId,
    startDate,
    teamIds,
    endDate,
  }) {
    const teamString = teamIds && teamIds.length ? `&teamIds=cs.{${teamIds}}` : ''
    const startDateFormattedString = dates.getFormattedDate(startDate)
    const endDateFormattedString = dates.getFormattedDate(endDate)

    return this.get(
      `/reports/pacing_line_items?lineItemId=eq.${lineItemId}&and=(date.gte.${startDateFormattedString},date.lte.${endDateFormattedString})&publisherId=eq.${publisherId}${teamString}`,
    )
  },

  AT_RISK(request) {
    const { isAlert = true, params, publisherId, teamId } = request
    const limitString = isAlert ? 'limit=1&' : ''
    const teamString = teamId && teamId !== '-1' ? `&teamIds=cs.{${teamId}}` : ''
    const queryString = api.getParameters(params)

    return this.get(
      `/reports/pacing_line_items_summary?${limitString}publisherId=eq.${publisherId}${teamString}&${queryString}`,
    )
  },

  STUCK_IN_PAUSED(request) {
    const { isAlert = true, params, publisherId, teamId } = request
    const queryString = api.getParameters(params)
    const limitString = isAlert ? 'limit=1&' : ''
    const teamString = teamId && teamId !== '-1' ? `&teamIds=cs.{${teamId}}` : ''

    return this.get(
      `/reports/pacing_line_items_summary?${limitString}publisherId=eq.${publisherId}${teamString}&${queryString}`,
    )
  },

  STUCK_IN_READY(request) {
    const { isAlert = true, params, publisherId, teamId } = request
    const queryString = api.getParameters(params)
    const limitString = isAlert ? 'limit=1&' : ''
    const teamString = teamId && teamId !== '-1' ? `&teamIds=cs.{${teamId}}` : ''

    return this.get(
      `/reports/pacing_line_items_summary?${limitString}publisherId=eq.${publisherId}${teamString}&${queryString}`,
    )
  },

  MISSING_CREATIVES(request) {
    const { isAlert = true, params, publisherId, teamId } = request
    const queryString = api.getParameters(params)
    const limitString = isAlert ? 'limit=1&' : ''
    const teamString = teamId && teamId !== '-1' ? `&teamIds=cs.{${teamId}}` : ''

    return this.get(
      `/reports/pacing_line_items_summary?${limitString}publisherId=eq.${publisherId}${teamString}&${queryString}`,
    )
  },

  ZERO_IMPRESSIONS(request) {
    const { isAlert = true, params, publisherId, teamId } = request
    const queryString = api.getParameters(params)
    const limitString = isAlert ? 'limit=1&' : ''
    const teamString = teamId && teamId !== '-1' ? `&teamIds=cs.{${teamId}}` : ''

    return this.get(
      `/reports/pacing_line_items_summary?${limitString}publisherId=eq.${publisherId}${teamString}&${queryString}`,
    )
  },

  ENDING_SOON(request) {
    const { isAlert = true, params, publisherId, teamId } = request
    const queryString = api.getParameters(params)
    const limitString = isAlert ? 'limit=1&' : ''
    const teamString = teamId && teamId !== '-1' ? `&teamIds=cs.{${teamId}}` : ''

    return this.get(
      `/reports/pacing_line_items_summary?${limitString}publisherId=eq.${publisherId}${teamString}&${queryString}`,
    )
  },

  /**
   * Log a security audit event to the backend.
   * Used for privileged operations like alias mode that need IP logging.
   * @param {Object} event - The security event details
   * @param {string} event.action - The action (ALIAS_ACTIVATED, ALIAS_DEACTIVATED, etc.)
   * @param {string} event.actor - The user performing the action
   * @param {string} [event.target] - The target of the action
   * @param {Object} [event.metadata] - Additional context
   * @returns {Promise<Object>} The audit response
   */
  logSecurityAudit(event) {
    return this.post('/audit', event)
  },

  // ============================================================================
  // ENGAGEMENT MODEL ADMIN API METHODS
  // ============================================================================

  /**
   * Get all engagement types (admin only)
   * @returns {Promise<Object>} List of engagement types
   */
  getEngagementTypes() {
    return this.get('/admin/engagement/types')
  },

  /**
   * Create a new engagement type (admin only)
   * @param {Object} typeData - Engagement type data
   * @returns {Promise<Object>} The created engagement type
   */
  createEngagementType(typeData) {
    return this.post('/admin/engagement/types', typeData)
  },

  /**
   * Update an engagement type (admin only)
   * @param {string} typeId - Engagement type ID
   * @param {Object} typeData - Updated type data
   * @returns {Promise<Object>} The updated engagement type
   */
  updateEngagementType(typeId, typeData) {
    return this.patch(`/admin/engagement/types/${typeId}`, typeData)
  },

  /**
   * Delete an engagement type (admin only)
   * @param {string} typeId - Engagement type ID
   * @returns {Promise<Object>} Deletion confirmation
   */
  deleteEngagementType(typeId) {
    return this.delete(`/admin/engagement/types/${typeId}`)
  },

  /**
   * Get all contract types (admin only)
   * @returns {Promise<Object>} List of contract types
   */
  getContractTypes() {
    return this.get('/admin/contract/types')
  },

  /**
   * Create a new contract type (admin only)
   * @param {Object} typeData - Contract type data
   * @returns {Promise<Object>} The created contract type
   */
  createContractType(typeData) {
    return this.post('/admin/contract/types', typeData)
  },

  /**
   * Update a contract type (admin only)
   * @param {string} typeId - Contract type ID
   * @param {Object} typeData - Updated type data
   * @returns {Promise<Object>} The updated contract type
   */
  updateContractType(typeId, typeData) {
    return this.patch(`/admin/contract/types/${typeId}`, typeData)
  },

  /**
   * Delete a contract type (admin only)
   * @param {string} typeId - Contract type ID
   * @returns {Promise<Object>} Deletion confirmation
   */
  deleteContractType(typeId) {
    return this.delete(`/admin/contract/types/${typeId}`)
  },

  /**
   * Get all assignment rules (admin only)
   * @returns {Promise<Object>} List of assignment rules
   */
  getAssignmentRules() {
    return this.get('/admin/assignment/rules')
  },

  /**
   * Create a new assignment rule (admin only)
   * @param {Object} ruleData - Assignment rule data
   * @returns {Promise<Object>} The created assignment rule
   */
  createAssignmentRule(ruleData) {
    return this.post('/admin/assignment/rules', ruleData)
  },

  /**
   * Update an assignment rule (admin only)
   * @param {string} ruleId - Assignment rule ID
   * @param {Object} ruleData - Updated rule data
   * @returns {Promise<Object>} The updated assignment rule
   */
  updateAssignmentRule(ruleId, ruleData) {
    return this.patch(`/admin/assignment/rules/${ruleId}`, ruleData)
  },

  /**
   * Delete an assignment rule (admin only)
   * @param {string} ruleId - Assignment rule ID
   * @returns {Promise<Object>} Deletion confirmation
   */
  deleteAssignmentRule(ruleId) {
    return this.delete(`/admin/assignment/rules/${ruleId}`)
  },

  /**
   * Sync engagement data from production to current environment (admin only)
   * @returns {Promise<Object>} Sync status
   */
  syncEngagementDataFromProduction() {
    return this.post('/admin/engagement/sync-production', {})
  },
}

export default api
